import { useMutation, useQuery } from '@apollo/client/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { InvitationStatus } from '@zeus/index'
import { Loader } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'
import { BrandLogo } from '~/components/BrandLogo'
import RequiredFieldsHint from '~/components/RequiredFieldsHint'
import { MAIN_ID } from '~/components/SkipLink'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormMessage } from '~/components/ui/form'
import { Label } from '~/components/ui/label'
import { PasswordInput } from '~/components/ui/password-input'
import { GQL_FINISH_REGISTER, GQL_INVITATION } from '~/gql/auth/auth'

/**
 * Finalisation d'un compte pré-inscrit par un admin d'org (#134).
 *
 * Cette page n'ouvre PLUS d'inscription libre : sans `?token=` elle n'a rien à
 * finaliser et renvoie vers le login. Le compte doit déjà exister côté API, la
 * mutation `finishRegister` ne fait que lui poser un mot de passe.
 *
 * Elle ne redemande plus l'identité (#17) : le jeton la résout auprès de l'API,
 * qui la connaît déjà. Les ressaisir n'avait aucun intérêt et deux défauts —
 * l'email saisi servait de clé de recherche, donc une faute de frappe faisait
 * échouer l'activation ; et les valeurs saisies écrasaient celles de
 * l'invitation.
 */
function CreateAccount() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  const token = searchParams.get('token')

  const { data, loading: resolving, error: resolveError } = useQuery(GQL_INVITATION, {
    variables: { token: token ?? '' },
    skip: !token,
    // Le jeton est consommé par la mutation : un cache servi au remontage
    // afficherait une invitation déjà utilisée comme encore valide.
    fetchPolicy: 'network-only',
  })

  const createAccountSchema = z.object({
    password: z.string().min(1, { message: t('input.password') }),
    confirmPassword: z.string().min(1, { message: t('input.password-confirm') }),
  }).refine(d => d.password === d.confirmPassword, {
    message: t('password.not-match.message'),
    path: ['confirmPassword'],
  })

  type CreateAccountFormValues = z.infer<typeof createAccountSchema>

  const form = useForm<CreateAccountFormValues>({
    resolver: zodResolver(createAccountSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })

  const [finishRegister, { loading }] = useMutation(GQL_FINISH_REGISTER)

  const onSubmit = async (values: CreateAccountFormValues) => {
    if (!token) return

    try {
      const { data: result } = await finishRegister({
        variables: {
          password: values.password,
          token,
        },
      })

      if (result?.finishRegister === InvitationStatus.VALID) {
        toast.success(t('good.register.message'))
        navigate('/login')
        return
      }

      toast.error(
        result?.finishRegister === InvitationStatus.EXPIRED
          ? t('invitation.expired.message')
          : t('bad.register.message'),
      )
    } catch {
      toast.error(t('bad.register.message'))
    }
  }

  if (!token) return <Navigate to="/login" replace />

  const invitation = data?.invitation

  /** Carte de bout de course : le jeton ne mène à aucun formulaire. */
  const notice = (title: string, description: string) => (
    <main id={MAIN_ID} className="flex w-full min-h-dvh flex-col items-center justify-center p-5">
      <Card className="sm:w-md w-full">
        <CardHeader className="gap-4">
          <BrandLogo className="h-9 w-auto" fallbackClassName="text-lg" />
          <div className="flex flex-col gap-1">
            <CardTitle as="h1" className="font-heading text-2xl tracking-tight">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <Button className="w-full" onClick={() => navigate('/login')}>
            {t('common.login')}
          </Button>
        </CardContent>
      </Card>
    </main>
  )

  if (resolving) {
    return (
      <main id={MAIN_ID} className="flex w-full min-h-dvh flex-col items-center justify-center p-5">
        <Spinner className="size-6" />
      </main>
    )
  }

  // API injoignable : on ne laisse pas la page tourner indéfiniment, mais on ne
  // prétend pas non plus que le lien est mauvais.
  if (resolveError || !invitation) {
    return notice(t('an.error.occurred'), t('bad.server-login.message'))
  }

  // Un jeton périmé et un jeton inventé ne se disent pas de la même façon : le
  // premier appelle une nouvelle invitation, le second est probablement une URL
  // tronquée. Un jeton déjà consommé ressort `INVALID` — l'API ne dit jamais
  // qu'un compte porte cette adresse.
  if (invitation.status === InvitationStatus.EXPIRED) {
    return notice(t('invitation.expired'), t('invitation.expired.message'))
  }
  if (invitation.status !== InvitationStatus.VALID) {
    return notice(t('invitation.invalid'), t('invitation.invalid.message'))
  }

  return (
    <main id={MAIN_ID} className="flex w-full min-h-dvh flex-col items-center justify-center p-5">
      <Card className="sm:w-xl w-full">
        <CardHeader className="gap-4">
          <BrandLogo className="h-9 w-auto" fallbackClassName="text-lg" />
          <div className="flex flex-col gap-1">
            <CardTitle as="h1" className="font-heading text-2xl tracking-tight">{t('finish.register')}</CardTitle>
            <CardDescription>
              {t('finish.register.message', {
                firstName: invitation.firstName,
                lastName: invitation.lastName,
                email: invitation.email,
              })}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
              <RequiredFieldsHint />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="w-full mx-auto">
                    <div className="grid gap-2">
                      <Label htmlFor="password" required>{t('common.password')}</Label>
                      <FormControl>
                        <PasswordInput
                          id="password"
                          aria-required
                          autoComplete="new-password"
                          // Premier champ du seul formulaire de l'écran, ouvert depuis le lien d'invitation.
                          // eslint-disable-next-line jsx-a11y/no-autofocus
                          autoFocus
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem className="w-full mx-auto">
                    <div className="grid gap-2">
                      <Label htmlFor="confirmPassword" required>{t('password.confirm')}</Label>
                      <FormControl>
                        <PasswordInput
                          id="confirmPassword"
                          aria-required
                          autoComplete="new-password"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={loading} className="w-full">
                {loading && <Loader className="mr-2 size-4 animate-spin" />}
                {t('common.register')}
              </Button>

              <div className="mt-4 text-center text-sm">
                <Link to="/login" className="underline underline-offset-4">
                  {t('common.login')}
                </Link>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </main>
  )
}

export default CreateAccount
